/**
 * CR315: Hook for fetching public visibility stats
 * 
 * Provides a unified view of deal visibility breakdown from the
 * public-deals-available endpoint. Used by Admin UI to show counts.
 */

import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { VisibilityStats, RetentionPolicyMode } from "@/lib/deals/publicVisibility";

export interface PublicVisibilityResponse {
  visibility_stats: VisibilityStats;
  policy: {
    mode: RetentionPolicyMode;
    grace_days: number;
  };
  deal_count: number;
  active_snapshot: string | null;
  as_of: string | null;
}

export interface UsePublicVisibilityStatsResult {
  stats: VisibilityStats | null;
  policy: { mode: RetentionPolicyMode; grace_days: number } | null;
  dealCount: number;
  activeSnapshot: string | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch public visibility stats from the public-deals-available endpoint.
 * This provides the same visibility breakdown that drives the public site.
 */
export function usePublicVisibilityStats(): UsePublicVisibilityStatsResult {
  const [stats, setStats] = useState<VisibilityStats | null>(null);
  const [policy, setPolicy] = useState<{ mode: RetentionPolicyMode; grace_days: number } | null>(null);
  const [dealCount, setDealCount] = useState(0);
  const [activeSnapshot, setActiveSnapshot] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke<PublicVisibilityResponse>(
        "public-deals-available"
      );

      if (fnError) {
        console.error("[usePublicVisibilityStats] Function error:", fnError);
        setError(fnError.message);
        return;
      }

      if (data?.visibility_stats) {
        setStats(data.visibility_stats);
      }
      if (data?.policy) {
        setPolicy(data.policy);
      }
      setDealCount(data?.deal_count ?? 0);
      setActiveSnapshot(data?.active_snapshot ?? null);

      console.log("[usePublicVisibilityStats] CR315: Fetched visibility stats:", data?.visibility_stats);
    } catch (err) {
      console.error("[usePublicVisibilityStats] Unexpected error:", err);
      setError(String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    stats,
    policy,
    dealCount,
    activeSnapshot,
    loading,
    error,
    refetch,
  };
}
